/**
 * Rejects sentence-start bold like **I need** so it is not used as a section title.
 * @param {string} inner
 * @param {string} textAfterBlock
 * @returns {boolean}
 */
function isLikelyBoldSectionTitle(inner, textAfterBlock) {
	const t = String(inner || '').trim();
	if (t.length < 2) return false;
	if (
		/^I (need|am|ve|’ve|will|can|should|was|have|think)\b/i.test(t) ||
		/^We (need|are|ve|’ve|will|can|should|were|have|think)\b/i.test(t)
	) {
		return false;
	}
	if (t.includes(' ')) return true;
	if (t.length >= 10) return true;
	if (textAfterBlock && !/^\s*$/.test(textAfterBlock) && t.length < 5) return false;
	if (t.length < 3) return false;
	return t.length >= 6;
}

/**
 * Strip heading markers and wrapping emphasis from a title string.
 * @param {string} raw
 * @returns {string}
 */
function cleanTitle(raw) {
	let t = String(raw || '').trim();
	t = t.replace(/^#{1,6}\s+/, '');
	const trail = t.match(/(\s#+)$/);
	if (trail) t = t.slice(0, -trail[0].length).trim();
	t = t.replace(/^\*+|\*+$/g, '').trim();
	t = t.replace(/\s+/g, ' ');
	return t;
}

/**
 * First finished line, only if it is short enough to be a title.
 * @param {string} text
 * @param {boolean} streaming
 * @returns {string|null}
 */
function extractFinishedFirstLine(text, streaming) {
	const trimmed = String(text || '').trimStart();
	if (!trimmed) return null;
	const nl = trimmed.indexOf('\n');
	let line = '';
	if (nl === -1) {
		if (streaming) return null;
		line = trimmed.split(/\s*[.!?]\s/)[0] || trimmed;
	} else {
		line = trimmed.slice(0, nl);
	}
	const title = cleanTitle(line);
	if (!title || title.length > 90) return null;
	return title;
}

/**
 * Latest complete markdown title from thinking text, or a finished first line.
 * Incomplete tokens while streaming keep the previous / default label.
 * @param {string} text
 * @param {boolean} streaming
 * @returns {string|null}
 */
export function extractThinkingTitle(text, streaming) {
	if (!text || !String(text).trim()) return null;
	const s = String(text);
	let best = null;
	/**
	 * Keep the title that ends latest in the stream.
	 * @param {number} end
	 * @param {string} title
	 */
	function consider(end, title) {
		const t = cleanTitle(title);
		if (!t) return;
		if (!best || end > best.end) best = { end: end, title: t };
	}

	const atxG = /(?:^|\n)(\s*#{1,6}\s+)([^\n]+)/g;
	let m = atxG.exec(s);
	while (m) {
		const lineEnd = m.index + m[0].length;
		const lineComplete = s.charAt(lineEnd) === '\n';
		if (!streaming || lineComplete) consider(lineEnd, m[2]);
		m = atxG.exec(s);
	}

	const boldG = /\*\*([^*]+)\*\*/g;
	m = boldG.exec(s);
	while (m) {
		const inner = m[1];
		const after = s.slice(m.index + m[0].length);
		if (isLikelyBoldSectionTitle(inner, after)) consider(m.index + m[0].length, inner);
		m = boldG.exec(s);
	}

	if (best) return best.title;
	return extractFinishedFirstLine(s, streaming);
}
